import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import Button from '@material-ui/core/Button';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import CustomDialog from 'components/CustomDialog/CustomDialog';

import happy from './happy.jpg';
import sad from './sad.jpg';
import { StyledIconButton } from './styled';

interface Props {
    open: boolean;
    onClose: () => void;
    assessmentId: string;
    showUpdateStateDialog?: () => void;
    showAuditButton: boolean;
}

const SelfAssessmentDialog = ({ open, onClose, assessmentId, showUpdateStateDialog, showAuditButton }: Props) => {
    const { t } = useTranslation();
    const history = useHistory();

    const questions = useMemo(() => {
        return Array.from({ length: 13 }, (_, i) => i + 1).map((i) => {
            return t('dialogs.self_assessment.questions.' + i);
        });
    }, [t]);

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [negativeQuestions, setNegativeQuestions] = useState<string[]>([]);

    useEffect(() => {
        if (!open) return;

        setCurrentQuestion(0);
        setNegativeQuestions([]);
    }, [open]);

    const answerYes = useCallback(() => {
        setCurrentQuestion(curr => curr + 1);
    }, []);

    const answerNo = useCallback(() => {
        setNegativeQuestions((curr: string[]) => {
            return [...curr, questions[currentQuestion]];
        });

        setCurrentQuestion(curr => curr + 1);
    }, [currentQuestion, questions]);

    return (
        <CustomDialog onClose={() => onClose()} open={open} fullWidth alwaysVisible>
            <DialogTitle className='dialog-title-text-ellipsis'>
                {t('dialogs.self_assessment.title')}

                <StyledIconButton onClick={onClose}>
                    <CloseIcon />
                </StyledIconButton>
            </DialogTitle>

            <DialogContent dividers>
                <link rel='preload' as='image' href={happy} />
                <link rel='preload' as='image' href={sad} />

                {(currentQuestion < questions.length) ? (
                    <>
                        <Typography variant='body1'>
                            {questions[currentQuestion]}
                        </Typography>

                        <div style={{ textAlign: 'center', marginTop: '16px' }}>
                            <Button variant='outlined' color='primary' style={{ width: '100px', padding: '10px 15px', margin: '8px', fontSize: '1.15em' }} onClick={answerNo}>
                                {t('dialogs.self_assessment.answers.no')}
                            </Button>

                            <Button variant='outlined' color='primary' style={{ width: '100px', padding: '10px 15px', margin: '8px', fontSize: '1.15em' }} onClick={answerYes}>
                                {t('dialogs.self_assessment.answers.yes')}
                            </Button>
                        </div>
                    </>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        {negativeQuestions.length === 0 ? (
                            <>
                                <img src={happy} style={{ maxWidth: '120px', marginBottom: '12px' }} alt='happy face' />

                                <Typography variant='h1' style={{ fontSize: '1.5em', fontWeight: '400', marginBottom: '12px' }} >
                                    {t('dialogs.self_assessment.success_message')}
                                </Typography>

                                {showAuditButton && (
                                    <Button variant='contained' color='primary' style={{ padding: '12px 15px', margin: '18px auto 8px', fontSize: '1.10em', display: 'block' }} disabled={!showUpdateStateDialog} onClick={() => {
                                        onClose();

                                        if (showUpdateStateDialog) showUpdateStateDialog();
                                    }}>
                                        {t('dialogs.self_assessment.buttons.audit_request')}
                                    </Button>
                                )}

                                <Button variant='outlined' color='primary' style={{ margin: '12px auto 0', display: 'block' }} onClick={() => onClose()}>
                                    {t('dialogs.self_assessment.buttons.close')}
                                </Button>
                            </>
                        ) : (
                            <>
                                <img src={sad} style={{ maxWidth: '120px', marginBottom: '12px' }} alt='sad face' />

                                <Typography variant='h1' style={{ fontSize: '1.5em', fontWeight: '400', marginBottom: '12px' }}>
                                    {t('dialogs.self_assessment.fail_message')}
                                </Typography>
                                <Typography variant='body1'>
                                    {t('dialogs.self_assessment.fail_description')}
                                </Typography>
                                <Typography variant='body1' style={{ margin: '18px 0', textAlign: 'left', fontSize: '1em' }}>
                                    <ul style={{ paddingInlineStart: '14px' }}>
                                        {negativeQuestions.map(q => <li>{q}</li>)}
                                    </ul>
                                </Typography>
                                <Button variant='contained' color='primary' style={{ padding: '12px 15px', margin: '18px auto 8px', fontSize: '1.10em', display: 'block' }} disabled={!assessmentId} onClick={() => {
                                    onClose();
                                    history.push('/plastic-assessments/' + assessmentId + '/pa');
                                }}>
                                    {t('dialogs.self_assessment.buttons.go_to_assessment')}
                                </Button>
                                <Button variant='outlined' color='primary' style={{ margin: '12px auto 0', display: 'block' }} onClick={() => onClose()}>
                                    {t('dialogs.self_assessment.buttons.close')}
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </CustomDialog>
    );
};

export default SelfAssessmentDialog;